import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import { Text, View, Image, ScrollView, Platform } from 'react-native';
import { Level, BigBodyGroup, WorkoutSettings } from 'fitbeat.gym-core';

import { styles, getResponsiveStyleSheet } from './Styles';
import WorkoutSettingSection from './WorkoutSettingSection';
import { getBodyGroupImage } from '../../utils/bodyGroupImage';
import { ExpertLevelIcon, BalanceIcon, BurnIcon, BuildIcon, BeginnerLevelIcon,
  IntermediateLevelIcon, BodyweightIcon, DumbbellsIcon } from '../../ui/components/Icons';
import Theme from '../../theme';
import BodyGroups from './BodyGroups';
import { mappedSkillLevelTitle } from '../../ui/components/SkillLevelList';
import { EquipmentList } from '../../ui/components';
import { isLarge } from '../../utils';
import { CardioOrResistance, EnabledFeatures, GymTypes } from 'fitbeat.models';
const responsive = getResponsiveStyleSheet();

class PersonaliseWorkoutSettings extends Component {
  constructor(props) {
    super(props);

    const { workoutSettings } = props;
    this.state = {
      workoutSettings: new WorkoutSettings(workoutSettings),
    };
  }

  componentDidUpdate = async (prevProps) => {
    if (JSON.stringify(prevProps.workoutSettings) !== JSON.stringify(this.props.workoutSettings)) {
      await this.setState({ workoutSettings: this.props.workoutSettings });
    }
  }

  changeLevelToBegginer = () => {
    const { workoutSettings } = this.state;
    workoutSettings.level = Level.Beginner;
    this.setState({ workoutSettings });
    this.props.onCompleteSettingChange(workoutSettings);
  }

  changeLevelToBasic = () => {
    const { workoutSettings } = this.state;
    workoutSettings.level = Level.Basic;
    this.setState({ workoutSettings });
    this.props.onCompleteSettingChange(workoutSettings);
  }

  changeLevelToIntermediate = () => {
    const { workoutSettings } = this.state;
    workoutSettings.level = Level.Intermediate;
    this.setState({ workoutSettings });
    this.props.onCompleteSettingChange(workoutSettings);
  }

  changeToCardio = () => {
    const { workoutSettings } = this.state;
    const { threeBodyGroups } = workoutSettings;
    workoutSettings.cardioOrResistance = CardioOrResistance.Cardio;
    if (threeBodyGroups.length >= 2) {
      workoutSettings.threeBodyGroups = [BigBodyGroup.Upper];
      this.completedBodyGroups([BigBodyGroup.BackAndArms, BigBodyGroup.Lower, BigBodyGroup.Upper])
    }
    this.setState({ workoutSettings });
    this.props.onCompleteSettingChange(workoutSettings);
  }

  changeToResistance = () => {
    const { workoutSettings } = this.state;
    const { threeBodyGroups } = workoutSettings;
    workoutSettings.cardioOrResistance = CardioOrResistance.Resistance;

    if (threeBodyGroups.length >= 2) {
      workoutSettings.threeBodyGroups = [BigBodyGroup.Upper];
      this.completedBodyGroups([BigBodyGroup.BackAndArms, BigBodyGroup.Lower, BigBodyGroup.Upper])
    }

    this.setState({ workoutSettings });
    this.props.onCompleteSettingChange(workoutSettings);
  }

  changeToBalance = () => {
    const { workoutSettings } = this.state;
    const { threeBodyGroups } = workoutSettings;
    workoutSettings.cardioOrResistance = CardioOrResistance.Balance;
    this.completedBodyGroups([BigBodyGroup.BackAndArms, BigBodyGroup.Lower, BigBodyGroup.Upper])
    this.setState({ workoutSettings });
    this.props.onCompleteSettingChange(workoutSettings);
  }

  completedBodyGroups = (bodyGroups) => {
    const { workoutSettings } = this.state;

    bodyGroups.sort();
    workoutSettings.threeBodyGroups = bodyGroups;
    this.setState({ workoutSettings });
    this.props.onCompleteSettingChange(workoutSettings);
  }

  toggleEquipment = (workoutEquipment) => {
    const { workoutSettings } = this.state;
    workoutSettings.equipment = workoutEquipment.sort();
    this.setState({ workoutSettings });
    this.props.onCompleteSettingChange(workoutSettings);
  }

  render() {
    const { t, userProfile: { gender }, gym,
      mainContainerStyle = {},
      settingSectionContainer = {},
      bodyGroupContainer = {},
      skillLevelContainer = {},
      workoutTypeContainer = {},
      levelSectionContainer = {},
      workoutTypeSectionContainer = {},
      showBodyGroupHeaderAboveOptions = false,
      enabledFeatures,
    } = this.props
    const { workoutSettings } = this.state;
    const { equipment = [] } = workoutSettings;
    const isHomeGym = gym.type === GymTypes.Home;
    const disableBodyGroupCustomization = WorkoutSettings.isBodyGroupsCustomizationDisabled(workoutSettings, gym.type);
    return (
      <ScrollView>
        <View style={mainContainerStyle}>
          <View style={settingSectionContainer}>
            <View style={[responsive.settingsContainer, skillLevelContainer]}>
              <View style={styles.settingsSavedContainer}>
                <Text style={styles.settingsHeader}>{t('skillLevel').toUpperCase()}</Text>
              </View>
              <View style={[styles.innerSettingsSectionContainer, responsive.skillLevelRowContainer]}>
                <View style={[styles.settingContainerWithDescription, levelSectionContainer]}>
                  <WorkoutSettingSection
                    isActiveSetting={workoutSettings.level === Level.Beginner}
                    onPress={this.changeLevelToBegginer}
                    subtitle={isLarge ? t('beginner') : null}
                    title={mappedSkillLevelTitle[Level.Beginner].short}
                    icon={<BeginnerLevelIcon color={Theme.BLACK} width={25} height={25} />}
                    containerStyle={{ flexBasis: '0%' }}
                  />
                  {!isLarge && <Text style={[styles.settingsSubtitle, styles.settingDescription]}>{t('beginner')}</Text>}
                </View>
                <View style={[styles.settingContainerWithDescription, levelSectionContainer]}>
                  <WorkoutSettingSection
                    isActiveSetting={workoutSettings.level === Level.Basic}
                    onPress={this.changeLevelToBasic}
                    title={mappedSkillLevelTitle[Level.Basic].short}
                    containerStyle={{ flexBasis: '0%' }}
                    subtitle={isLarge ? t('intermediate') : null}
                    icon={<IntermediateLevelIcon color={Theme.BLACK} width={25} height={25} />}
                  />
                  {!isLarge && <Text style={[styles.settingsSubtitle, styles.settingDescription]}>{t('intermediate')}</Text>}
                </View>
                <View style={styles.settingContainerWithDescription}>
                  <WorkoutSettingSection
                    isActiveSetting={workoutSettings.level === Level.Intermediate}
                    onPress={this.changeLevelToIntermediate}
                    containerStyle={{ flexBasis: '0%' }}
                    subtitle={isLarge ? t('experienced') : null}
                    title={mappedSkillLevelTitle[Level.Intermediate].short}
                    icon={<ExpertLevelIcon color={Theme.BLACK} width={25} height={25} />}
                  />
                  {!isLarge && <Text style={[styles.settingsSubtitle, styles.settingDescription]}>{t('experienced')}</Text>}
                </View>
              </View>
            </View>
            <View style={[responsive.settingsContainer, workoutTypeContainer]}>
              <Text style={styles.settingsHeader}>{t('workoutType').toUpperCase()}</Text>
              <View style={styles.innerSettingsSectionContainer}>
                <View style={responsive.leftSettingsContainer}>
                  <WorkoutSettingSection
                    isActiveSetting={workoutSettings.cardioOrResistance === CardioOrResistance.Cardio}
                    onPress={this.changeToCardio}
                    title={t('burn')}
                    subtitle={t('burnSubtitle', { context: Platform.OS })}
                    icon={<BurnIcon color={Theme.BLACK} width={25} height={25} />}
                    containerStyle={[responsive.workoutSettingsContainer, workoutTypeSectionContainer]}
                  />
                </View>
                  <WorkoutSettingSection
                    isActiveSetting={workoutSettings.cardioOrResistance === CardioOrResistance.Resistance}
                    onPress={this.changeToResistance}
                    title={t('build')}
                    subtitle={t('buildSubtitle', { context: Platform.OS })}
                    icon={<BuildIcon color={Theme.BLACK} width={25} height={25} />}
                  />
                  <WorkoutSettingSection
                    isActiveSetting={workoutSettings.cardioOrResistance === CardioOrResistance.Balance}
                    onPress={this.changeToBalance}
                    title={t('balance')}
                    subtitle={t('balanceSubtitle', { context: Platform.OS })}
                    icon={<BalanceIcon color={Theme.BLACK} width={25} height={25} />}
                  />
              </View>
            </View>
          </View>
          <View style={[settingSectionContainer, bodyGroupContainer]} pointerEvents={disableBodyGroupCustomization ? 'none' : 'auto'}>
            <View style={responsive.settingsContainer}>
              {!showBodyGroupHeaderAboveOptions &&
                <Text style={styles.settingsHeader}>{t('bodyGroups').toUpperCase()}</Text>
              }
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}>
                <View style={styles.bodyGroupImageContainer}>
                  <Image
                    resizeMode='contain'
                    source={getBodyGroupImage(workoutSettings.threeBodyGroups, workoutSettings.cardioOrResistance, gender)}
                    style={styles.bodyGroupImage}
                  />
                </View>
                <View style={styles.bodyGroupSelectionContainer}>
                  {showBodyGroupHeaderAboveOptions &&
                    <Text style={[styles.settingsHeader, { marginBottom: 20 }]}>{t('bodyGroups').toUpperCase()}</Text>
                  }
                  <BodyGroups
                    workoutSettings={workoutSettings}
                    disable={disableBodyGroupCustomization}
                    setBodyGroups={this.completedBodyGroups}
                  />
                </View>
              </View>
              {workoutSettings.cardioOrResistance === CardioOrResistance.Cardio &&
                <Text style={[styles.settingsSubtitle, styles.fullBodyEmphasis]}>{t('fullBodyWithEmphasis')}</Text>
              }
              {(isHomeGym && !EnabledFeatures.canSelectEquipment(enabledFeatures)) && 
                <Text style={[styles.settingsSubtitle, styles.fullBodyEmphasis]}>{t('homeWorkoutFullBodyOnly')}</Text>
              }
            </View>
          </View>
        </View>
        {(isHomeGym && EnabledFeatures.canSelectEquipment(enabledFeatures)) && (<View style={responsive.settingsContainer}>
          <Text style={styles.settingsHeader}>{t('equipment').toUpperCase()}</Text>
          <EquipmentList
            noBorder
            currentSelectedEquipment={equipment}
            onCompleteEquipmentChange={this.toggleEquipment}
            firstContainerStyling={responsive.workoutSettingsContainer}
          />
        </View>)}

      </ScrollView>
    );
  }
}

PersonaliseWorkoutSettings.propTypes = {
  userProfile: PropTypes.object.isRequired,
  onCompleteSettingChange: PropTypes.func.isRequired,
  workoutSettings: PropTypes.object.isRequired,
};

export default translate(['bookings'], { wait: true })(PersonaliseWorkoutSettings);
